import { useCallback, useEffect, useRef, useState } from "react";

import { getCursorList } from "@/providers/dataProvider/dataProvider";
import { ExtractedRecord } from "@/types";

import { IndexedExtractedRecord, stampAbsoluteIndicesFromWindow } from "../utils/recordIndexing";

/**
 * Cursor-paged extracted-record fetch (#6755).
 *
 * ## THIS RETURNS A WINDOW, NOT THE DOCUMENT. READ THIS BEFORE CONSUMING IT.
 *
 * `records` is the pages loaded so far. On any document above
 * REVIEW_AUTOLOAD_MAX_RECORDS that is a fraction of the set — 1,000 of
 * 497,710 on the document this exists for.
 *
 * So a question of the form "does this document contain X", "what is the
 * highest Y", "how many Z" CANNOT be answered from this array. Answering it
 * anyway produces a confident wrong answer, not an error, and the failure is
 * always a FALSE NEGATIVE: the thing is there, on a page nobody fetched.
 *
 * Where whole-document answers actually come from:
 *
 *   - counts, per-schema counts, records-with-errors, max-rows-per-claimant
 *     -> `transformation.processing_metadata.record_summary`, written by the
 *        worker over the complete set at finalize
 *   - "which records share a claimant with these"
 *     -> POST .../records/claimant-linkage (#6848), server-side
 *   - "which records carry schema X", with a truthful total
 *     -> GET .../records?target_schema=X (#6893)
 *   - anything else
 *     -> it needs a worker-computed flag on record_summary, or an endpoint.
 *        Not a loop over this array.
 *
 * If neither is available and you must reason over the window, DISCLOSE THE
 * SCOPE in the UI. The precedents are the search box ("Searched the 1,000
 * loaded records of 497,710") and the stale-as-of check
 * (`staleCheckPartial`). Both stayed loaded-scoped and said so, which turns a
 * silent wrong answer into a visible partial one — the difference between a
 * reviewer being misled and a reviewer being informed.
 *
 * This is not hypothetical guidance. The same mistake has been found five
 * times since paging shipped: the exclusion cascade (#6848, a money outcome —
 * claim lines loaded against a claimant the reviewer had removed), search
 * (#6899), the errored-record view (#6853), the claimant expand panel
 * (#6900), and three any-record predicates catalogued in #6901. **#6901 is
 * the closed list of every consumer and its classification — check it before
 * adding a sixth.**
 *
 * Reads `GET /documents/{documentId}/document-transformations/{transformationId}/records`,
 * which returns one page of records each carrying its ABSOLUTE `record_index`
 * — read from the stored column server-side, never inferred from a result
 * position.
 *
 * This is the read path `?expand=extracted_records` cannot serve. That query
 * returns the entire set in one response; at 497,711 records the request
 * itself times out before the browser draws anything, so the document
 * persists (after #6749) but still cannot be reviewed, approved, or loaded.
 *
 * ## Paging model
 *
 * Reads through the dataProvider's `getCursorList` (#6849), not `useGetList`.
 * `getList` is page-number based — it turns `{page, perPage}` into
 * `?limit&offset` — and this endpoint deliberately rejects `?offset`, because
 * on this table offset does not skip cheaply. Measured on a 497,711-record
 * transformation:
 *
 *	OFFSET 497500   195.4 ms   101,995 buffers  (~800 MB of I/O for 200 rows)
 *	from_record_index 0.12 ms       44 buffers
 *
 * The index scan walks every preceding row and heap-fetches its record
 * payload, so the server exposes an inclusive cursor and no offset at all.
 *
 * This hook previously called `httpClient` directly and carried a note
 * explaining the deviation from the list-fetching convention. #6849 replaced
 * that with a real provider capability, so the truncation guarantee the
 * convention exists to enforce (#4478: six weeks of silently capped lists)
 * now lives in ONE place — `getCursorList` throws if a response claims
 * `has_more` without supplying a cursor, rather than reading it as the end.
 */

/**
 * Page size. The server caps a page at 1,000 regardless of what is asked for,
 * so this asks for the cap: one request covers any document a reviewer has
 * actually worked through, and large documents page from there.
 *
 * Numerically equal to the dataProvider's PAGE_CHUNK but NOT derived from it,
 * deliberately: PAGE_CHUNK is the offset-paging chunk the provider slices
 * useGetList reads into, while this is the keyset endpoint's server-side page
 * cap. They bound different protocols and can diverge independently — tying
 * them together would let a provider tuning change silently under- or
 * over-ask this endpoint. Same reasoning for REVIEW_AUTOLOAD_MAX_RECORDS vs
 * DEFAULT_MAX_RECORDS below (a review round-trip budget vs the provider's
 * generic list ceiling).
 */
export const TRANSFORMATION_RECORDS_PAGE_SIZE = 1000;

/**
 * Below this many records the hook loads EVERY page before reporting ready, so
 * the review surface behaves exactly as it did before #6755 — all records in
 * hand, all counts derivable, no "load more" affordance.
 *
 * The number is a round-trip budget, not a memory one: 5,000 records is at most
 * five sequential requests. It sits far above any document anyone has reviewed
 * (the largest real one before this work was a few thousand rows) and far below
 * the scale that motivated the story.
 */
export const REVIEW_AUTOLOAD_MAX_RECORDS = 5000;

/**
 * Above this many records the review tab does NOT request the review
 * projection.
 *
 * Separate constant from REVIEW_AUTOLOAD_MAX_RECORDS on purpose — they bound
 * different things. That one is a client round-trip budget; this one is a
 * server memory limit: POST review-projection materialises the whole record set
 * inside mgmt-api (2.34 GiB measured at 497,711 records, against a 1Gi
 * container — #6826). Set higher than the auto-load bound so documents that
 * work fine today keep their forecast chips.
 */
export const REVIEW_PROJECTION_MAX_RECORDS = 25000;

/**
 * Cap on the SPAN of one windowed projection request, mirroring the server's
 * `projectionWindowMaxRecords` clamp (documents.go). KEEP IN SYNC.
 *
 * The projection window is `{from: firstLoadedIndex, limit: loadedCount}`, and
 * loadedCount grows by a page per "Load more". Without this cap the request
 * grows past the server clamp at ~10 pages and the clamp silently truncates
 * the span — rows past it render with no forecast chip and nothing says why.
 * Capping here makes client and server agree by construction, and the review
 * surface can disclose "forecasts cover the first N loaded records" instead
 * of thinning out silently.
 */
export const REVIEW_PROJECTION_WINDOW_MAX_RECORDS = 10000;

interface UseTransformationRecordsProps {
  documentId: string | undefined;
  transformationId: string | undefined;
  pageSize?: number;
  enabled?: boolean;
  /**
   * Load every page automatically while the document's total is at or below
   * this. Set to 0 to always page on demand.
   */
  autoLoadUpTo?: number;
}

interface UseTransformationRecordsResult {
  /** Records loaded so far, in record_index order, each carrying its index. */
  records: ExtractedRecord[];
  /** Total records stored for the transformation, across all pages. */
  total: number;
  /**
   * True once `total` is authoritative — i.e. the first page's response (which
   * carries the server-side count of the WHOLE set) has landed for the current
   * transformation. Distinguishes "0 records" from "haven't asked yet": `total`
   * is `useState(0)`, and a size gate that reads it before this is true would
   * conclude "small" about a document it knows nothing about (#6755 review).
   */
  totalKnown: boolean;
  /** True while any page request is outstanding. */
  isLoading: boolean;
  error: unknown;
  /** Whether another page exists. */
  hasMore: boolean;
  /** Load the next page and append it. No-op while loading or exhausted. */
  loadMore: () => void;
  /**
   * True once every page has been loaded, or once the first page has arrived
   * for a document being paged on demand. The review surface waits on this
   * rather than on isLoading, so an auto-loading document does not render a
   * half-loaded grid mid-sequence.
   */
  isReady: boolean;
  /**
   * Load a page starting at a specific absolute index, replacing what is held.
   *
   * The endpoint's `from_record_index` is inclusive and index-addressed, so
   * jumping to a record costs the same as reading the first page — there is no
   * page number to compute and no offset to walk.
   */
  jumpTo: (fromRecordIndex: number) => void;
}

export const useTransformationRecords = ({
  documentId,
  transformationId,
  pageSize = TRANSFORMATION_RECORDS_PAGE_SIZE,
  enabled = true,
  autoLoadUpTo = REVIEW_AUTOLOAD_MAX_RECORDS,
}: UseTransformationRecordsProps): UseTransformationRecordsResult => {
  const [records, setRecords] = useState<ExtractedRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [totalKnown, setTotalKnown] = useState(false);
  const [cursor, setCursor] = useState<number | null>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // Identity guard for responses, NOT an in-flight lock. Every fetch takes the
  // next generation; a response only writes state if no newer fetch has started
  // since it left. That is what keeps a superseded request's records out:
  // without it, a reprocess that swaps transformationId while a page is in
  // flight can land the OLD transformation's records — carrying the old run's
  // record_index values — under the new one, and everything downstream trusts
  // those indices absolutely (excluded_record_indices to the worker, forecast
  // joins). Plausible numbers, wrong rows, nothing on screen to reveal it.
  //
  // An in-flight boolean cannot do this job: it guarded ordering, but its
  // early-return silently DROPPED the reset fetch fired on a transformation
  // change, leaving the old records in state with nothing left to refetch.
  // Superseding subsumes the ordering concern — two overlapping appends
  // resolve to the newer one writing and the older one being discarded.
  const generation = useRef(0);

  const ready = enabled && !!documentId && !!transformationId;

  const fetchPage = useCallback(
    async (from: number, mode: "append" | "replace") => {
      if (!ready) return;
      const gen = ++generation.current;
      setIsLoading(true);
      setError(null);

      try {
        const page = await getCursorList<IndexedExtractedRecord>(
          `documents/${documentId}/document-transformations/${transformationId}/records`,
          { from, limit: pageSize }
        );
        // A newer fetch started while this one was in flight — this response
        // describes a request nobody is waiting on. Discard it entirely; the
        // newer fetch owns all state, including isLoading.
        if (gen !== generation.current) return;

        // The server's record_index is moved onto each record here and read
        // everywhere after. Nothing downstream recomputes it from a position
        // in this page — see recordIndexing.ts for why that matters.
        const stamped = stampAbsoluteIndicesFromWindow(page.data);

        setRecords((prev) => (mode === "append" ? [...prev, ...stamped] : stamped));
        setTotal(page.total);
        setTotalKnown(true);
        setCursor(page.nextCursor);
      } catch (err) {
        if (gen === generation.current) setError(err);
      } finally {
        if (gen === generation.current) setIsLoading(false);
      }
    },
    [ready, documentId, transformationId, pageSize]
  );

  // First page. Resets when the transformation changes, so a reprocess does
  // not leave the previous run's records appended underneath.
  useEffect(() => {
    if (!ready) return;
    setRecords([]);
    setTotal(0);
    setTotalKnown(false);
    setCursor(0);
    void fetchPage(0, "replace");
  }, [ready, documentId, transformationId, fetchPage]);

  const loadMore = useCallback(() => {
    if (cursor === null) return;
    void fetchPage(cursor, "append");
  }, [cursor, fetchPage]);

  const jumpTo = useCallback(
    (fromRecordIndex: number) => {
      void fetchPage(Math.max(0, fromRecordIndex), "replace");
    },
    [fetchPage]
  );

  const hasMore = cursor !== null;

  // Small documents load completely before the surface renders, so nothing
  // downstream has to distinguish "all records" from "the first page". Driven
  // off `total`, which the first page reports, so the decision is made on the
  // real size rather than guessed from the page that came back.
  const autoLoading = hasMore && total > 0 && total <= autoLoadUpTo;

  useEffect(() => {
    if (!autoLoading || isLoading || error) return;
    void fetchPage(cursor as number, "append");
  }, [autoLoading, isLoading, error, cursor, fetchPage]);

  return {
    records,
    total,
    totalKnown,
    isLoading,
    error,
    hasMore,
    loadMore,
    jumpTo,
    // Ready once the first page has landed and any auto-load sequence has
    // finished. Deliberately NOT gated on isLoading (#6755 review): a "Load
    // more" on the on-demand path used to flip this false, which unmounted the
    // whole grid — discarding selection, search text and scroll position — and
    // replaced it with the loading gate for the duration of the request.
    // Ready is about having a coherent set to show, not about being idle.
    isReady: ready && totalKnown && !autoLoading,
  };
};
